import React, { useState, useEffect } from "react";
import './forgot.css';

// Legacy: single OTP + password reset using /api/usr-reset-password
// Password policy simplified to (>=8 chars, letter & number) to match legacy theme.
const LEGACY_PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#]{8,}$/;

export default function ForgotPasswordVerify({ showAlert }) {
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [toastQueue, setToastQueue] = useState([]); // {id,type,msg}
  const [successMsg, setSuccessMsg] = useState("");

  // retrieve username stored during request step
  let storedUsername = '';
  try { storedUsername = localStorage.getItem('forgot_username') || ''; } catch(_) {}

  const canSubmit = otp.length > 0 && LEGACY_PASSWORD_REGEX.test(newPassword) && newPassword === confirmPassword && storedUsername;

  const changePassword = async () => {
    setError("");
    setSuccessMsg("");
    if (!otp) { setError("Please enter the OTP."); return; }
    if (!LEGACY_PASSWORD_REGEX.test(newPassword)) { setError("Password must include letter, number & be 8+ chars."); return; }
    if (!confirmPassword) { setError("Please confirm your new password."); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    if (!storedUsername) { setError("Username missing. Restart reset process."); return; }

    setLoading(true);
    try {
      const payload = { otp: otp.trim(), password: newPassword, password_confirmation: confirmPassword, username: storedUsername.trim() };
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usr-reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to change password.');
      const msg = 'Password changed successfully. Please login with your new password.';
      setSuccessMsg(msg);
      enqueueToast('success', msg);
      showAlert?.(msg);
      setTimeout(() => (window.location.href = '/login'), 1500);
    } catch (err) {
      const finalErr = err?.message || 'Error during password change.';
      setError(finalErr);
      enqueueToast('error', finalErr);
    } finally { setLoading(false); }
  };

  const enqueueToast = (type, msg) => {
    const id = Date.now() + Math.random();
    setToastQueue(q => [...q, { id, type, msg }]);
    setTimeout(() => {
      setToastQueue(q => q.filter(t => t.id !== id));
    }, 3500);
  };

  useEffect(()=>{ if(error && !toastQueue.find(t=>t.msg===error)) enqueueToast('error', error); },[error]);

  const otpClass = `fp-input ${error && error.toLowerCase().includes('otp') ? 'fp-input-error' : ''}`;
  const newPassClass = `fp-input ${error && error.toLowerCase().includes('password') ? 'fp-input-error' : ''}`;
  const confirmClass = `fp-input ${error && error.toLowerCase().includes('match') ? 'fp-input-error' : ''}`;

  return (
    <div className="fp-wrap" style={{ maxWidth: 420, margin: '20px auto' }}>
      <h3 className="fp-title">Reset password</h3>
      {storedUsername && <div className="fp-note">Account: {storedUsername}</div>}
      {error && <div className="fp-error" role="alert" aria-live="assertive">{error}</div>}
      {successMsg && <div className="fp-info" role="status" aria-live="polite">{successMsg}</div>}
      <div className="fp-form" style={{ display:'flex', flexDirection:'column', gap:8 }}>
        <label className="fp-label">OTP</label>
        <input className={otpClass} value={otp} maxLength={6} onChange={(e)=> setOtp(e.target.value.replace(/\D/g,''))} placeholder="Enter 6 Digit OTP" />
        <label className="fp-label">New Password</label>
        <input className={newPassClass} type="password" value={newPassword} onChange={(e)=> setNewPassword(e.target.value)} placeholder="Enter New Password" />
        <label className="fp-label">Confirm Password</label>
        <input className={confirmClass} type="password" value={confirmPassword} onChange={(e)=> setConfirmPassword(e.target.value)} placeholder="Confirm Password" />
        <ul className="tips" style={{margin:'8px 0', color:'#999', fontSize:12}}>
          <li>Min 8 characters</li>
          <li>Include at least one letter & one number</li>
        </ul>
        <button disabled={!canSubmit || loading} className="fp-submit" onClick={changePassword}>
          {loading && <span className="fp-spinner" />}
          {loading ? 'Processing...' : 'Confirm'}
        </button>
      </div>
      {toastQueue.length > 0 && (
        <div className="fp-toast" role="status" aria-live="polite">
          {toastQueue.map(t => (
            <div key={t.id} className={`fp-toast-item ${t.type === 'success' ? 'fp-toast-success' : 'fp-toast-error'}`}> 
              <span className="fp-toast-icon">{t.type === 'success' ? '✓' : '⚠'}</span>
              <span>{t.msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
